import { useCallback, useMemo } from 'react';

import { useLocalStorage } from '@kc-console/core';

export interface ScreenTableSetting {
  columnOrder?: string[];
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  columnSizing?: Record<string, number>;
  columnVisibility?: Record<string, boolean>;
  compactMode?: boolean;
  pageSize?: number;
  textEllipsis?: boolean;
}

type TableSettings = Record<string, ScreenTableSetting>;

const TABLE_SETTING_SCHEMA_VERSION = 'v1.0';
const TABLE_SETTINGS_KEY = 'kc-table-settings';
const DEFAULT_TABLE_SETTINGS: ScreenTableSetting = {
  columnOrder: [],
  columnPinning: {},
  columnSizing: {},
  columnVisibility: {},
  textEllipsis: true,
  compactMode: false,
};

export default function useTableSettings(
  storageKey?: string,
  initialValues?: Partial<ScreenTableSetting>,
) {
  const [config, setConfig] = useLocalStorage<TableSettings>(
    TABLE_SETTINGS_KEY,
    undefined,
    TABLE_SETTING_SCHEMA_VERSION,
  );

  const screenConfig = useMemo(() => {
    // SSR에서는 항상 기본값 사용하여 hydration mismatch 방지
    if (typeof window === 'undefined') {
      return DEFAULT_TABLE_SETTINGS;
    }

    const validConfig = storageKey ? config?.[storageKey] : {};

    return {
      ...DEFAULT_TABLE_SETTINGS,
      ...initialValues,
      ...(validConfig ?? {}),
    };
  }, [config, storageKey, initialValues]);

  const setScreenConfig = useCallback(
    (nextScreenConfig: Partial<ScreenTableSetting>) => {
      if (!storageKey) {
        return;
      }

      setConfig((prev = {}) => ({
        ...prev,
        [storageKey]: {
          ...prev[storageKey],
          ...nextScreenConfig,
        },
      }));
    },
    [setConfig, storageKey],
  );

  return [screenConfig, setScreenConfig] as const;
}
